/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation as useRouterLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardMedia,
  Grid,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Delete,
  Save,
  PhotoCamera,
  Description,
  LocationOn,
  MyLocation,
  ArrowBack,
  Mic,
  Stop,
  PlayArrow,
  Pause,
  Videocam,
  Close,
  Map,
  Place,
  Archive,
  Update,
  CameraAlt,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeBr } from '../utils/datetime';
import PlantaViewer from '../components/PlantaViewer';

const STATUS_OPTIONS = [
  { value: 'aberto', label: 'Aberto' },
  { value: 'em_andamento', label: 'Em Andamento' },
  { value: 'aguardando_resposta', label: 'Aguardando Resposta' },
  { value: 'pausado', label: 'Pausado' },
  { value: 'resolvido', label: 'Resolvido' },
  { value: 'fechado', label: 'Fechado' },
];

const PRIORIDADE_OPTIONS = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

const TIPO_OPTIONS = [
  { value: 'suporte', label: 'Suporte Técnico' },
  { value: 'solicitacao', label: 'Solicitação' },
  { value: 'reclamacao', label: 'Reclamação' },
  { value: 'duvida', label: 'Dúvida' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'reforma', label: 'Reforma' },
  { value: 'obra', label: 'Obra' },
  { value: 'projetos', label: 'Projetos' },
  { value: 'emergencia', label: 'Emergência' },
  { value: 'outro', label: 'Outro' },
];

const WHATSAPP_MEDIA_KIND_LABEL = {
  audio: 'Áudio',
  document: 'Arquivo',
  image: 'Foto',
  video: 'Vídeo',
};

const getPrefillWhatsappMediaList = (prefill) => {
  const media = prefill?.whatsappMessage?.media;

  if (Array.isArray(media)) return media.filter((entry) => entry && typeof entry === 'object');
  return media && typeof media === 'object' ? [media] : [];
};

const formatPrefillWhatsappMediaSize = (value) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const resolvePrefillWhatsappMediaUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (typeof window === 'undefined') return value;

  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return value;
  }
};

function WhatsappPrefillMediaPreview({ media }) {
  if (!media) return null;

  const mediaUrl = resolvePrefillWhatsappMediaUrl(media.url);
  const sizeLabel = formatPrefillWhatsappMediaSize(media.byte_size || media.file_length);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Chip size="small" label={WHATSAPP_MEDIA_KIND_LABEL[media.kind] || 'Mídia'} color="primary" variant="outlined" />
          {media.file_name ? <Chip size="small" label={media.file_name} variant="outlined" /> : null}
          {sizeLabel ? <Chip size="small" label={sizeLabel} variant="outlined" /> : null}
        </Stack>

        {media.kind === 'image' && mediaUrl ? (
          <Box
            component="img"
            src={mediaUrl}
            alt={media.file_name || 'Imagem recebida via WhatsApp'}
            sx={{
              width: '100%',
              maxWidth: 320,
              borderRadius: 2,
              border: '1px solid rgba(148,163,184,0.35)',
              objectFit: 'cover',
            }}
          />
        ) : null}

        {media.kind === 'video' && mediaUrl ? (
          <Box
            component="video"
            src={mediaUrl}
            controls
            sx={{
              width: '100%',
              maxWidth: 320,
              borderRadius: 2,
              border: '1px solid rgba(148,163,184,0.35)',
              backgroundColor: '#0f172a',
            }}
          />
        ) : null}

        {media.kind === 'audio' && mediaUrl ? <Box component="audio" src={mediaUrl} controls sx={{ width: '100%', maxWidth: 320 }} /> : null}

        {media.kind === 'document' && mediaUrl ? (
          <Button component="a" href={mediaUrl} target="_blank" rel="noreferrer" size="small" variant="outlined" sx={{ width: 'fit-content' }}>
            Abrir arquivo recebido
          </Button>
        ) : null}

        {!mediaUrl && media.storage_error ? (
          <Typography variant="caption" color="warning.main">
            A mídia foi identificada, mas não pôde ser armazenada: {media.storage_error}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function ExistingFotoCard({ url, api, editId, token, onOpen }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!url) return undefined;

    let active = true;
    let objectUrl = '';
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    fetch(`${api}/chamados/${editId}/anexo?path=${encodeURIComponent(url)}`, { headers })
      .then((response) => {
        if (!response.ok) throw new Error('Falha ao carregar foto');
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, editId, token, url]);

  return (
    <Card
      onClick={() => onOpen(url)}
      title="Clique para ampliar"
      sx={{
        position: 'relative',
        width: 82,
        height: 82,
        overflow: 'hidden',
        cursor: 'zoom-in',
        border: '1px solid rgba(25, 118, 210, 0.45)',
        bgcolor: 'grey.100',
        '&:hover': { boxShadow: 4, transform: 'scale(1.03)' },
      }}
    >
      {src && !failed ? (
        <CardMedia component="img" height="82" image={src} sx={{ objectFit: 'cover' }} />
      ) : (
        <Box sx={{ height: 82, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'text.secondary', textAlign: 'center', px: 0.5 }}>
          {failed ? 'Erro ao carregar' : 'Carregando...'}
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          py: 0.2,
          color: '#fff',
          bgcolor: 'rgba(0,0,0,0.58)',
          fontSize: 10,
          textAlign: 'center',
          fontWeight: 700,
        }}
      >
        AMPLIAR
      </Box>
    </Card>
  );
}

export default function AbrirChamado({ isModal = false, onSuccess: _onSuccess, initialPrefill = null }) {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { id: editId } = useParams();
  const { token, logout } = useAuth();
  const onSuccess = typeof _onSuccess === 'function' ? _onSuccess : null;
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const isEditMode = Boolean(editId);

  // Dados do chamado
  const [clienteId, setClienteId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [status, setStatus] = useState('aberto');
  const [prioridade, setPrioridade] = useState('media');
  const [tipo, setTipo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [prazoConclusao, setPrazoConclusao] = useState('');

  // Localização (GPS)
  const [useLocation, setUseLocation] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);

  // Arquivos
  const [fotos, setFotos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [existingFotos, setExistingFotos] = useState([]);
  const [deletedExistingFotos, setDeletedExistingFotos] = useState([]);
  const [fotoParaExcluir, setFotoParaExcluir] = useState(null);
  const [fotoVisualizacao, setFotoVisualizacao] = useState(null);
  const [fotoVisualizacaoSrc, setFotoVisualizacaoSrc] = useState('');
  const [existingDocs, setExistingDocs] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);

  // Gravação de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcricao, setTranscricao] = useState('');
  const [transcrevendo, setTranscrevendo] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const recognitionRef = useRef(null);

  const fotoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const docInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const cameraVideoInputRef = useRef(null);
  const monitorPrefillAppliedRef = useRef('');

  // Planta Baixa - Multi-plantas
  const [plantaImagem, setPlantaImagem] = useState(null);
  const [plantaMarker, setPlantaMarker] = useState(null);
  const [showPlantaDialog, setShowPlantaDialog] = useState(false);
  const [plantaChamados, setPlantaChamados] = useState([]);
  const [plantasList, setPlantasList] = useState([]);
  const [selectedPlantaId, setSelectedPlantaId] = useState('');
  const [plantaArquivadaInfo, setPlantaArquivadaInfo] = useState(null);

  const handleUnauthorized = () => {
    setSnack({
      open: true,
      message: 'Sua sessão expirou. Faça login novamente para registrar o criador do chamado.',
      severity: 'warning',
    });
    logout();
    setTimeout(() => navigate('/login', { replace: true }), 800);
  };

  const API = '/api';
  const activePrefill = !isEditMode ? initialPrefill || routerLocation.state?.prefill || null : null;
  const prefillWhatsappMessage = activePrefill?.whatsappMessage || null;
  const prefillWhatsappMedia = getPrefillWhatsappMediaList(activePrefill);
  const aberturaRegistradaEm = React.useMemo(() => new Date().toISOString(), []);

  // Carregar clientes
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Verificar clientes permitidos
        let clienteIds = null;
        try {
          const meRes = await fetch(`${API}/me/clientes`, { headers: authHeaders });
          if (meRes.ok) {
            const meData = await meRes.json();
            clienteIds = meData.clientes || [];
          }
        } catch (e) {
          console.warn('Endpoint /me/clientes não disponível');
        }

        const res = await fetch(`${API}/clientes`, { headers: authHeaders });
        if (res.ok) {
          let data = await res.json();
          if (clienteIds && clienteIds.length > 0) {
            data = data.filter((c) => clienteIds.map(Number).includes(Number(c.id)));
          }
          setClientes(data);
        }
      } catch (err) {
        console.error('Erro ao buscar clientes:', err);
      }
    };
    fetchClientes();
  }, [token, API]);

  // Carregar plantas do cliente selecionado
  useEffect(() => {
    if (!clienteId) {
      setPlantaImagem(null);
      setPlantaChamados([]);
      setPlantasList([]);
      setSelectedPlantaId('');
      setPlantaArquivadaInfo(null);
      return;
    }
    const fetchPlanta = async () => {
      try {
        const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API}/clientes/${clienteId}/planta_chamados`, { headers: authHeaders });
        if (res.ok) {
          const data = await res.json();
          const plantas = (data.plantas || []).filter((p) => !p.arquivada);
          setPlantasList(plantas);
          setPlantaChamados(data.chamados || []);
          // Retrocompat: use first planta image or legacy field
          if (plantas.length > 0) {
            setPlantaImagem(plantas[0].planta_imagem);
            setSelectedPlantaId(plantas[0].id);
          } else {
            setPlantaImagem(data.planta_imagem);
            setSelectedPlantaId('');
          }
        }
      } catch (e) {
        console.warn('Erro ao buscar planta:', e);
      }
    };
    fetchPlanta();
  }, [clienteId, token, API]);

  // Carregar dados para edição
  useEffect(() => {
    if (isEditMode && editId) {
      setLoadingData(true);
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      fetch(`${API}/chamados/${editId}`, { headers: authHeaders })
        .then((res) => res.json())
        .then((data) => {
          setClienteId(data.cliente_id || '');
          setTitulo(data.titulo || '');
          setDescricao(data.descricao || '');
          setStatus(data.status || 'aberto');
          setPrioridade(data.prioridade || 'media');
          setTipo(data.tipo || '');
          setObservacoes(data.observacoes || '');
          setPrazoConclusao(data.prazo_conclusao || '');
          
          if (data.latitude && data.longitude) {
            setLocation({ latitude: data.latitude, longitude: data.longitude });
            setUseLocation(true);
          }
          
          if (data.planta_x != null && data.planta_y != null) {
            setPlantaMarker({ x: data.planta_x, y: data.planta_y });
          }
          if (data.planta_id) {
            setSelectedPlantaId(data.planta_id);
            // Check if planta is archived
            if (data.planta_arquivada) {
              setPlantaArquivadaInfo({
                nivel_display: data.planta_nivel_display || '',
                arquivada_em: data.planta_arquivada_em || '',
              });
            }
          }
          
          if (Array.isArray(data.fotos)) {
            setExistingFotos(data.fotos);
            setDeletedExistingFotos([]);
          }
          if (Array.isArray(data.documentos)) {
            setExistingDocs(data.documentos);
          }
          if (Array.isArray(data.videos)) {
            setExistingVideos(data.videos);
          }
        })
        .catch((err) => {
          console.error('Erro ao carregar chamado:', err);
          setSnack({ open: true, message: 'Erro ao carregar chamado', severity: 'error' });
        })
        .finally(() => setLoadingData(false));
    }
  }, [isEditMode, editId, token, API]);

  useEffect(() => {
    if (isEditMode) return;

    const prefill = activePrefill;
    if (!prefill) return;

    const prefillSignature = prefill.whatsappMessage?.id
      ? `wa:${prefill.whatsappMessage.id}`
      : JSON.stringify([
          prefill.clienteId || '',
          prefill.titulo || '',
          prefill.descricao || '',
          prefill.observacoes || '',
        ]);

    if (monitorPrefillAppliedRef.current === prefillSignature) return;

    setClienteId(prefill.clienteId || '');
    setTitulo(prefill.titulo || '');
    setDescricao(prefill.descricao || '');
    setPrioridade(prefill.prioridade || 'media');
    setTipo(prefill.tipo || '');
    setObservacoes(prefill.observacoes || '');
    setPrazoConclusao(prefill.prazoConclusao || new Date().toISOString().split('T')[0]);
    monitorPrefillAppliedRef.current = prefillSignature;
  }, [activePrefill, isEditMode]);

  // Capturar localização
  const captureLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocalização não suportada pelo navegador');
      return;
    }

    setLocationLoading(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationLoading(false);
      },
      (error) => {
        let msg = 'Erro ao obter localização';
        if (error.code === 1) msg = 'Permissão de localização negada';
        else if (error.code === 2) msg = 'Localização indisponível';
        else if (error.code === 3) msg = 'Tempo esgotado ao obter localização';
        setLocationError(msg);
        setLocationLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Gravação de áudio com transcrição em tempo real
  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);

      // Iniciar transcrição em tempo real com Web Speech API
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'pt-BR';

        let finalTranscript = '';

        recognitionRef.current.onresult = (event) => {
          let interimTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }
          setTranscricao(finalTranscript + interimTranscript);
        };

        recognitionRef.current.onerror = (event) => {
          console.warn('Erro no reconhecimento de voz:', event.error);
        };

        recognitionRef.current.start();
        setTranscrevendo(true);
      }
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      setSnack({ open: true, message: 'Não foi possível acessar o microfone', severity: 'error' });
    }
  };

  const pararGravacao = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    // Parar transcrição
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
      setTranscrevendo(false);
    }
  };

  const playPauseAudio = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
      } else {
        audioPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const removerAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setTranscricao('');
    setIsPlaying(false);
    setTranscrevendo(false);
  };

  // Handlers de arquivo
  const handleFotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    setFotos((prev) => {
      const remaining = 10 - (existingFotos.length + prev.length);
      if (remaining <= 0) {
        setSnack({ open: true, message: 'Limite de 10 fotos atingido.', severity: 'warning' });
        return prev;
      }
      const allowed = files.slice(0, remaining);
      if (files.length > remaining) {
        setSnack({ open: true, message: `Apenas ${remaining} foto(s) adicionada(s). Limite de 10 atingido.`, severity: 'warning' });
      }
      return [...prev, ...allowed.map((file) => ({ file, preview: URL.createObjectURL(file) }))];
    });
    e.target.value = '';
  };

  const handleDocChange = (e) => {
    const files = Array.from(e.target.files || []);
    setDocumentos((prev) => {
      const remaining = 10 - (existingDocs.length + prev.length);
      if (remaining <= 0) {
        setSnack({ open: true, message: 'Limite de 10 documentos atingido.', severity: 'warning' });
        return prev;
      }
      const allowed = files.slice(0, remaining);
      if (files.length > remaining) {
        setSnack({ open: true, message: `Apenas ${remaining} documento(s) adicionado(s). Limite de 10 atingido.`, severity: 'warning' });
      }
      return [...prev, ...allowed.map((file) => ({ file, name: file.name }))];
    });
    e.target.value = '';
  };

  const removeFoto = (index) => {
    setFotos((prev) => prev.filter((_, i) => i !== index));
  };

  const confirmarExclusaoFotoExistente = () => {
    const url = fotoParaExcluir;
    if (!url) return;

    setExistingFotos((prev) => prev.filter((item) => item !== url));
    setDeletedExistingFotos((prev) => (prev.includes(url) ? prev : [...prev, url]));
    setFotoParaExcluir(null);
    if (fotoVisualizacao === url) {
      setFotoVisualizacao(null);
      setFotoVisualizacaoSrc('');
    }
  };

  const abrirFotoVisualizacao = async (url) => {
    setFotoVisualizacao(url);
    setFotoVisualizacaoSrc('');

    try {
      const res = await fetch(`${API}/chamados/${editId}/anexo?path=${encodeURIComponent(url)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) throw new Error('Falha ao carregar foto');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      setFotoVisualizacaoSrc((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });
    } catch (err) {
      console.error('Erro ao ampliar foto:', err);
      setSnack({ open: true, message: 'Erro ao carregar foto para ampliar', severity: 'error' });
    }
  };

  const fecharFotoVisualizacao = () => {
    setFotoVisualizacao(null);
    setFotoVisualizacaoSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const removeDoc = (index) => {
    setDocumentos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files || []);
    const MAX_VIDEO_SIZE = 40 * 1024 * 1024; // 40MB
    
    const validFiles = files.filter((file) => {
      if (file.size > MAX_VIDEO_SIZE) {
        setSnack({
          open: true,
          message: `Vídeo "${file.name}" excede 40MB. Máximo permitido: 40MB`,
          severity: 'warning',
        });
        return false;
      }
      return true;
    });

    setVideos((prev) => {
      const remaining = 10 - (existingVideos.length + prev.length);
      if (remaining <= 0) {
        setSnack({ open: true, message: 'Limite de 10 vídeos atingido.', severity: 'warning' });
        return prev;
      }
      const allowed = validFiles.slice(0, remaining);
      if (validFiles.length > remaining) {
        setSnack({ open: true, message: `Apenas ${remaining} vídeo(s) adicionado(s). Limite de 10 atingido.`, severity: 'warning' });
      }
      return [...prev, ...allowed.map((file) => ({ file, name: file.name, preview: URL.createObjectURL(file) }))];
    });
    e.target.value = '';
  };

  const removeVideo = (index) => {
    setVideos((prev) => {
      const video = prev[index];
      if (video?.preview) {
        URL.revokeObjectURL(video.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  // Salvar chamado
  const salvarChamado = async () => {
    if (!clienteId) {
      setSnack({ open: true, message: 'Selecione um cliente', severity: 'warning' });
      return;
    }
    if (!titulo.trim()) {
      setSnack({ open: true, message: 'Informe o título do chamado', severity: 'warning' });
      return;
    }
    if (!descricao.trim()) {
      setSnack({ open: true, message: 'Informe a descrição do chamado', severity: 'warning' });
      return;
    }
    if (!isEditMode && !prazoConclusao) {
      setSnack({ open: true, message: 'Informe o prazo para conclusão do chamado', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('cliente_id', clienteId);
      formData.append('titulo', titulo);
      formData.append('descricao', descricao);
      formData.append('status', status);
      formData.append('prioridade', prioridade);
      if (tipo) formData.append('tipo', tipo);
      if (observacoes) formData.append('observacoes', observacoes);
      if (prazoConclusao) formData.append('prazo_conclusao', prazoConclusao);

      if (!isEditMode && prefillWhatsappMessage?.id) {
        formData.append('origem', 'whatsapp');
        formData.append('whatsapp_message_id', String(prefillWhatsappMessage.id));
      }

      if (useLocation && location) {
        formData.append('latitude', location.latitude);
        formData.append('longitude', location.longitude);
      }

      // Posição na planta
      if (plantaMarker) {
        formData.append('planta_x', plantaMarker.x);
        formData.append('planta_y', plantaMarker.y);
      }
      if (selectedPlantaId) {
        formData.append('planta_id', selectedPlantaId);
      }

      // Adicionar áudio e transcrição
      if (audioBlob) {
        formData.append('audio', audioBlob, 'audio.webm');
      }
      if (transcricao.trim()) {
        formData.append('transcricao', transcricao);
      }

      // Adicionar fotos
      fotos.forEach((f) => {
        if (f.file) formData.append('fotos[]', f.file);
      });

      // Remover fotos existentes marcadas no modo edição
      deletedExistingFotos.forEach((url) => {
        formData.append('deleted_fotos[]', url);
      });

      // Adicionar documentos
      documentos.forEach((d) => {
        if (d.file) formData.append('documentos[]', d.file);
      });

      // Adicionar vídeos
      videos.forEach((v) => {
        if (v.file) formData.append('videos[]', v.file);
      });

      const url = isEditMode ? `${API}/chamados/${editId}` : `${API}/chamados`;
      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (res.ok) {
        const savedChamado = await res.json().catch(() => null);
        setSnack({
          open: true,
          message: isEditMode ? 'Chamado atualizado com sucesso!' : 'Chamado aberto com sucesso!',
          severity: 'success',
        });
        if (onSuccess) {
          onSuccess(savedChamado);
        } else {
          setTimeout(() => navigate('/chamados'), 1500);
        }
      } else if (res.status === 401) {
        handleUnauthorized();
      } else {
        const data = await res.json();
        setSnack({
          open: true,
          message: data.error || 'Erro ao salvar chamado',
          severity: 'error',
        });
      }
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setSnack({ open: true, message: 'Erro ao salvar chamado', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: isModal ? 1 : 3 }, maxWidth: 900, mx: 'auto' }}>
      <Paper elevation={isModal ? 0 : 3} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {!isModal && (
            <IconButton onClick={() => navigate('/chamados')} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight="bold">
            {isEditMode ? 'Editar Chamado' : 'Abrir Chamado'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          {prefillWhatsappMessage ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#f8fafc' }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <Chip size="small" color="primary" label="Origem: WhatsApp" />
                  {prefillWhatsappMessage.groupName ? <Chip size="small" variant="outlined" label={`Grupo: ${prefillWhatsappMessage.groupName}`} /> : null}
                  {prefillWhatsappMessage.senderName || prefillWhatsappMessage.senderPhone ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Remetente: ${prefillWhatsappMessage.senderName || prefillWhatsappMessage.senderPhone}`}
                    />
                  ) : null}
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  {prefillWhatsappMessage.capturedAt
                    ? `Capturado em ${formatDateTimeBr(prefillWhatsappMessage.capturedAt)}`
                    : 'Mensagem importada do monitor do agente.'}
                </Typography>

                {prefillWhatsappMessage.displayText ? (
                  <Typography variant="body2">{prefillWhatsappMessage.displayText}</Typography>
                ) : null}

                {prefillWhatsappMedia.length ? (
                  <Stack spacing={1.5}>
                    {prefillWhatsappMedia.map((media, index) => (
                      <WhatsappPrefillMediaPreview key={`${media.url || media.file_name || media.kind || 'media'}-${index}`} media={media} />
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </Paper>
          ) : null}

          {/* Cliente */}
          <TextField
            select
            label="Cliente *"
            value={clienteId}
            onChange={(e) => setClienteId(e.target.value)}
            fullWidth
          >
            {clientes.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.nome}
              </MenuItem>
            ))}
          </TextField>

          {/* Título */}
          <TextField
            label="Título do Chamado *"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            fullWidth
            placeholder="Descreva brevemente o problema ou solicitação"
          />

          {!isEditMode && (
            <TextField
              label="Data/Hora de Abertura"
              value={formatDateTimeBr(aberturaRegistradaEm)}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          )}

          <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
            {/* Tipo */}
            <TextField
              select
              label="Tipo do Chamado"
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              fullWidth
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: '100%' }}
            >
              <MenuItem value="">Selecione o tipo...</MenuItem>
              {TIPO_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Prioridade */}
            <TextField
              select
              label="Prioridade"
              value={prioridade}
              onChange={(e) => setPrioridade(e.target.value)}
              fullWidth
              sx={{ width: '100%' }}
            >
              {PRIORIDADE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/* Prazo para Conclusão */}
          <TextField
            label="Prazo para Conclusão *"
            type="date"
            value={prazoConclusao}
            onChange={(e) => setPrazoConclusao(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
          />

          {/* Status (só em edição) */}
          {isEditMode && (
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              fullWidth
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Descrição */}
          <TextField
            label="Descrição *"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            multiline
            rows={4}
            fullWidth
            placeholder="Descreva detalhadamente o problema, solicitação ou dúvida"
          />

          {/* Observações */}
          <TextField
            label="Observações"
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder="Informações adicionais (opcional)"
          />

          {/* Marcação na Planta Baixa */}
          {(plantaImagem || plantasList.length > 0) && (
            <>
              <Divider />
              <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                  <Map color="primary" />
                  Localização na Planta
                </Typography>
                {/* Alerta planta arquivada */}
                {plantaArquivadaInfo && (
                  <Alert
                    severity="warning"
                    icon={<Archive />}
                    sx={{ mb: 1.5 }}
                    action={
                      plantasList.length > 0 ? (
                        <Button
                          color="warning"
                          size="small"
                          startIcon={<Update />}
                          onClick={() => {
                            const mesmoNivel = plantasList.find(
                              (p) => p.nivel_display === plantaArquivadaInfo.nivel_display
                            );
                            const novaPlanta = mesmoNivel || plantasList[0];
                            if (novaPlanta && window.confirm(
                              `Deseja atualizar para a planta ativa "${novaPlanta.nivel_display}"?`
                            )) {
                              setSelectedPlantaId(novaPlanta.id);
                              setPlantaImagem(novaPlanta.planta_imagem);
                              setPlantaChamados(novaPlanta.chamados || []);
                              setPlantaMarker(null);
                              setPlantaArquivadaInfo(null);
                              setSnack({ open: true, message: 'Planta atualizada! Salve o chamado para confirmar.', severity: 'info' });
                            }
                          }}
                        >
                          Atualizar Planta
                        </Button>
                      ) : null
                    }
                  >
                    <strong>Planta Arquivada — {plantaArquivadaInfo.nivel_display}</strong>
                    <br />
                    Arquivada em: {plantaArquivadaInfo.arquivada_em || '—'}
                  </Alert>
                )}
                {plantasList.length > 1 && (
                  <TextField
                    select
                    label="Selecione a planta (nível)"
                    fullWidth
                    size="small"
                    value={selectedPlantaId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedPlantaId(id);
                      const p = plantasList.find((pl) => pl.id === id);
                      if (p) {
                        setPlantaImagem(p.planta_imagem);
                        setPlantaChamados(p.chamados || []);
                      }
                      setPlantaMarker(null);
                    }}
                    sx={{ mb: 1 }}
                  >
                    {plantasList.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.nivel_display}</MenuItem>
                    ))}
                  </TextField>
                )}
                {plantasList.length === 1 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Planta: <strong>{plantasList[0].nivel_display}</strong>
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Clique para marcar a localização do chamado na planta do cliente
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Place />}
                    onClick={() => setShowPlantaDialog(true)}
                  >
                    {plantaMarker ? 'Alterar Posição na Planta' : 'Marcar na Planta'}
                  </Button>
                  {plantaMarker && (
                    <>
                      <Chip
                        icon={<Place />}
                        label={`Posição: ${plantaMarker.x.toFixed(1)}%, ${plantaMarker.y.toFixed(1)}%`}
                        color="success"
                      />
                      <IconButton size="small" color="error" onClick={() => setPlantaMarker(null)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Stack>
              </Box>
            </>
          )}

          <Divider />

          {/* Localização GPS */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn color="primary" />
              Localização
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={useLocation}
                  onChange={(e) => {
                    setUseLocation(e.target.checked);
                    if (e.target.checked && !location) {
                      captureLocation();
                    }
                  }}
                />
              }
              label="Incluir localização GPS"
            />
            {useLocation && (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={locationLoading ? <CircularProgress size={16} /> : <MyLocation />}
                  onClick={captureLocation}
                  disabled={locationLoading}
                  size="small"
                >
                  {location ? 'Atualizar Localização' : 'Capturar Localização'}
                </Button>
                {location && (
                  <Chip
                    icon={<LocationOn />}
                    label={`${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`}
                    color="success"
                    sx={{ ml: 1 }}
                  />
                )}
                {locationError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {locationError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Divider />

          {/* Gravação de Áudio */}
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Mic color="primary" /> Gravação de Áudio com Transcrição Automática
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              {!isRecording ? (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Mic />}
                  onClick={iniciarGravacao}
                  disabled={audioBlob !== null}
                >
                  Gravar Áudio
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<Stop />}
                  onClick={pararGravacao}
                >
                  Parar Gravação
                </Button>
              )}
              {isRecording && (
                <>
                  <Chip label="Gravando..." color="error" variant="outlined" />
                  {transcrevendo && (
                    <Chip 
                      icon={<CircularProgress size={14} />} 
                      label="Transcrevendo..." 
                      color="info" 
                      variant="outlined" 
                    />
                  )}
                </>
              )}
            </Stack>

            {audioUrl && (
              <Box sx={{ mt: 2 }}>
                <audio ref={audioPlayerRef} src={audioUrl} onEnded={() => setIsPlaying(false)} style={{ display: 'none' }} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton onClick={playPauseAudio} color="primary">
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    Áudio gravado
                  </Typography>
                  <IconButton onClick={removerAudio} color="error" size="small">
                    <Delete />
                  </IconButton>
                </Stack>
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Transcrição do áudio (edite se necessário)"
              value={transcricao}
              onChange={(e) => setTranscricao(e.target.value)}
              placeholder="A transcrição do áudio aparecerá aqui ou você pode digitar manualmente..."
              sx={{ mt: 2 }}
            />
          </Box>

          <Divider />

          {/* Fotos */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhotoCamera color="primary" />
              Fotos
            </Typography>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fotoInputRef}
              style={{ display: 'none' }}
              onChange={handleFotoChange}
            />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={cameraInputRef}
              style={{ display: 'none' }}
              onChange={handleFotoChange}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<PhotoCamera />}
                onClick={() => fotoInputRef.current?.click()}
                disabled={(existingFotos.length + fotos.length) >= 10}
              >
                Adicionar Fotos
              </Button>
              <Button
                variant="outlined"
                startIcon={<CameraAlt />}
                onClick={() => cameraInputRef.current?.click()}
                disabled={(existingFotos.length + fotos.length) >= 10}
                color="secondary"
              >
                Tirar Foto
              </Button>
              <Typography variant="caption" color="text.secondary">
                {existingFotos.length + fotos.length}/10
              </Typography>
            </Box>
            
            {/* Fotos existentes */}
            {existingFotos.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                {existingFotos.map((url, i) => (
                  <Box key={`existing-${i}`} sx={{ width: 82, height: 82 }}>
                    <ExistingFotoCard
                      url={url}
                      api={API}
                      editId={editId}
                      token={token}
                      onOpen={abrirFotoVisualizacao}
                    />
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Novas fotos */}
            {fotos.length > 0 && (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {fotos.map((f, i) => (
                  <Grid item xs={4} sm={3} md={2} key={i}>
                    <Card sx={{ position: 'relative' }}>
                      <CardMedia component="img" height="80" image={f.preview} sx={{ objectFit: 'cover' }} />
                      <IconButton
                        size="small"
                        onClick={() => removeFoto(i)}
                        sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.8)' }}
                      >
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Divider />

          {/* Vídeos */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Videocam color="primary" />
              Vídeos (máx. 40MB cada)
            </Typography>
            <input
              type="file"
              accept="video/*"
              multiple
              ref={videoInputRef}
              style={{ display: 'none' }}
              onChange={handleVideoChange}
            />
            <input
              type="file"
              accept="video/*"
              capture="environment"
              ref={cameraVideoInputRef}
              style={{ display: 'none' }}
              onChange={handleVideoChange}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Videocam />}
                onClick={() => videoInputRef.current?.click()}
                disabled={(existingVideos.length + videos.length) >= 10}
              >
                Adicionar Vídeos
              </Button>
              <Button
                variant="outlined"
                startIcon={<Videocam />}
                onClick={() => cameraVideoInputRef.current?.click()}
                disabled={(existingVideos.length + videos.length) >= 10}
                color="secondary"
              >
                Gravar Vídeo
              </Button>
              <Typography variant="caption" color="text.secondary">
                {existingVideos.length + videos.length}/10
              </Typography>
            </Box>
            
            {/* Vídeos existentes */}
            {existingVideos.length > 0 && (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {existingVideos.map((url, i) => (
                  <Grid item xs={6} sm={4} md={3} key={`existing-video-${i}`}>
                    <Card>
                      <video
                        src={url}
                        controls
                        style={{ width: '100%', height: 120, objectFit: 'cover' }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
            
            {/* Novos vídeos */}
            {videos.length > 0 && (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {videos.map((v, i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}>
                    <Card sx={{ position: 'relative' }}>
                      <video
                        src={v.preview}
                        controls
                        style={{ width: '100%', height: 120, objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeVideo(i)}
                        sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.8)' }}
                      >
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                      <Typography variant="caption" sx={{ p: 0.5, display: 'block' }} noWrap>
                        {v.name}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Divider />

          {/* Documentos */}
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description color="primary" />
              Documentos
            </Typography>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.ppt,.pptx"
              multiple
              ref={docInputRef}
              style={{ display: 'none' }}
              onChange={handleDocChange}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Description />}
                onClick={() => docInputRef.current?.click()}
                disabled={(existingDocs.length + documentos.length) >= 10}
              >
                Adicionar Documentos
              </Button>
              <Typography variant="caption" color="text.secondary">
                {existingDocs.length + documentos.length}/10
              </Typography>
            </Box>
            
            {/* Documentos existentes */}
            {existingDocs.length > 0 && (
              <List dense>
                {existingDocs.map((doc, i) => (
                  <ListItem key={`existing-doc-${i}`}>
                    <ListItemText primary={doc.name || `Documento ${i + 1}`} />
                  </ListItem>
                ))}
              </List>
            )}
            
            {/* Novos documentos */}
            {documentos.length > 0 && (
              <List dense>
                {documentos.map((doc, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={doc.name} />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => removeDoc(i)}>
                        <Delete color="error" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          <Divider />

          {/* Botões */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate('/chamados')}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={salvarChamado}
              disabled={loading}
            >
              {isEditMode ? 'Salvar Alterações' : 'Abrir Chamado'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Foto ampliada para exclusão */}
      <Dialog
        open={Boolean(fotoVisualizacao)}
        onClose={fecharFotoVisualizacao}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: '#111', overflow: 'hidden' } }}
      >
        <DialogContent
          sx={{
            p: 0,
            minHeight: { xs: '65vh', md: '78vh' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            bgcolor: '#111',
          }}
        >
          {fotoVisualizacaoSrc ? (
            <Box
              component="img"
              src={fotoVisualizacaoSrc}
              alt="Foto ampliada"
              sx={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }}
            />
          ) : (
            <Typography sx={{ color: '#fff' }}>Carregando foto...</Typography>
          )}

          <IconButton
            aria-label="Apagar foto"
            title="Apagar foto"
            onClick={() => setFotoParaExcluir(fotoVisualizacao)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 3,
              width: 46,
              height: 46,
              color: '#fff',
              bgcolor: 'error.main',
              border: '3px solid #fff',
              boxShadow: 5,
              '&:hover': { bgcolor: 'error.dark', transform: 'scale(1.06)' },
            }}
          >
            <Close sx={{ fontSize: 30, fontWeight: 900 }} />
          </IconButton>

          <Button
            variant="contained"
            onClick={fecharFotoVisualizacao}
            sx={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              bgcolor: 'rgba(255,255,255,0.18)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
            }}
          >
            Fechar
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(fotoParaExcluir)} onClose={() => setFotoParaExcluir(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Tem certeza apagar a foto?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            A foto será removida ao salvar o chamado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFotoParaExcluir(null)}>Não</Button>
          <Button variant="contained" color="error" onClick={confirmarExclusaoFotoExistente}>
            Sim
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog da Planta para marcação */}
      <Dialog open={showPlantaDialog} onClose={() => setShowPlantaDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Map /> Marcar Localização na Planta
          {plantasList.length > 0 && (
            <Chip
              label={plantasList.find((p) => p.id === selectedPlantaId)?.nivel_display || ''}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Clique na planta para marcar o local do chamado
          </Typography>
          <PlantaViewer
            imagemUrl={plantaImagem}
            chamados={(() => {
              const sel = plantasList.find((p) => p.id === selectedPlantaId);
              return sel?.chamados || plantaChamados;
            })()}
            markerPosition={plantaMarker}
            editable
            onClick={(x, y) => setPlantaMarker({ x, y })}
            height={500}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPlantaMarker(null); setShowPlantaDialog(false); }} color="error">
            Remover Marcação
          </Button>
          <Button onClick={() => setShowPlantaDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setShowPlantaDialog(false)} disabled={!plantaMarker}>
            Confirmar Posição
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
